import { useCallback, useEffect, useMemo, useState } from 'react';

import { Box, MenuItem, TextField, debounce } from '@mui/material';
import {
  GridFilterInputValue,
  GridFilterInputValueProps,
} from '@mui/x-data-grid-pro';

export type CfsLocationFilterValue = {
  addressField?: string;
  value?: string;
};

const addressFieldOptions = [
  { label: 'All', value: 'all' },
  { label: 'House / Building Number', value: 'addressNumber' },
  { label: 'Street Name', value: 'completeStreetName' },
  { label: 'City', value: 'city' },
  { label: 'State', value: 'state' },
  { label: 'Zip Code', value: 'zipCode' },
  { label: 'Country', value: 'country' },
  { label: 'Cross Street', value: 'crossStreet' },
];

const getFilterValue = (itemValue: unknown): CfsLocationFilterValue => {
  if (itemValue && typeof itemValue === 'object') {
    const valueObject = itemValue as CfsLocationFilterValue;
    return {
      addressField: valueObject.addressField || 'all',
      value: valueObject.value || '',
    };
  }

  if (typeof itemValue === 'string') {
    return {
      addressField: 'all',
      value: itemValue,
    };
  }

  return { addressField: 'all', value: '' };
};

export const CFSLocationFilterInput = (props: GridFilterInputValueProps) => {
  const { addressField, value } = getFilterValue(props.item.value);
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const applyValue = useCallback(
    (nextAddressField: string, nextValue: string) => {
      props.applyValue({
        ...props.item,
        value: {
          addressField: nextAddressField,
          value: nextValue,
        },
      });
    },
    [props.applyValue, props.item],
  );

  const debouncedApplyValue = useMemo(
    () =>
      debounce((nextValue: string, nextAddressField: string) => {
        applyValue(nextAddressField, nextValue);
      }, 1000),
    [applyValue],
  );

  useEffect(() => {
    return () => {
      debouncedApplyValue.clear();
    };
  }, [debouncedApplyValue]);

  return (
    <>
      <Box sx={{ order: 2, minWidth: 150 }}>
        <TextField
          label="Field"
          select
          size="small"
          variant="standard"
          value={addressField}
          onChange={(event) => {
            // optional: commit immediately on field change
            debouncedApplyValue.clear();
            applyValue(String(event.target.value), localValue || '');
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        >
          {addressFieldOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box sx={{ order: 4, minWidth: 200, flex: 1 }}>
        <GridFilterInputValue
          {...props}
          value={localValue}
          onChange={(event) => {
            const nextValue = event.target.value;
            setLocalValue(nextValue);
            debouncedApplyValue(nextValue, addressField || 'all');
          }}
        />
      </Box>
    </>
  );
};
